// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

// d3.tip
// Copyright (c) 2013 Justin Palmer
//
// Tooltips for d3.js SVG visualizations

import {select, selection} from 'd3-selection'
import type {Selection} from 'd3-selection'

function getSVGNode(el: Selection): SVGSVGElement {
  const node = el.node()
  if (node.tagName.toLowerCase() === 'svg') {
    return node
  }

  return node.ownerSVGElement
}

interface BBox {
  nw: SVGPoint
  ne: SVGPoint
  se: SVGPoint
  sw: SVGPoint
  n: SVGPoint
  s: SVGPoint
  e: SVGPoint
  w: SVGPoint
}

type DatumCallback<T> = (datum: T) => string

export interface Tip<T> {
  (selection: Selection): void
  attr(name: string, value: string | null | DatumCallback<T>): Tip<T>
  offset(value: [number, number]): Tip<T>
  html(value: DatumCallback<T>): Tip<T>
  hide(): Tip<T>
  show: (...items: unknown[]) => Tip<T>
}

export default function d3Tip<T>(): Tip<T> {
  const node = initNode()
  let svg = null
  let point = null

  function offset() {
    return [0, 0]
  }
  function html() {
    return ' '
  }

  function tip(el: Selection) {
    svg = getSVGNode(el)
    point = svg.createSVGPoint()
    document.body.appendChild(node)
  }

  // Public - show the tooltip on the screen
  //
  // Returns a tip
  tip.show = function (event, d) {
    const content = html(d)
    const poffset = offset(d)
    const dir = 'n'
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
    const scrollLeft = document.documentElement.scrollLeft || document.body.scrollLeft

    node.innerHTML = content
    node.style.opacity = '1'
    node.style.pointerEvents = 'all'

    const coords = directionN(point, node, event.target)
    node.classList.toggle(dir, true)
    node.style.top = `${coords.top + poffset[0] + scrollTop}px`
    node.style.left = `${coords.left + poffset[1] + scrollLeft}px`

    return tip
  }

  // Public - hide the tooltip
  //
  // Returns a tip
  tip.hide = function () {
    node.style.opacity = '0'
    node.style.pointerEvents = 'none'
    return tip
  }

  // Public: Proxy attr calls to the d3 tip container.  Sets attribute value.
  //
  // n - name of the attribute
  // v - value of the attribute
  //
  // Returns the tip
  tip.attr = function (name: string, value: string | null | DatumCallback<T>) {
    selection.prototype.attr.apply(select(node), [name, value])
    return tip
  }

  // Public: Sets the offset of the tip
  //
  // Returns the tip
  tip.offset = function (value: [number, number]) {
    offset = function () {
      return value
    }

    return tip
  }

  // Public: sets the html value of the tooltip
  //
  // Returns the tip
  tip.html = function (value: DatumCallback<T>) {
    html = value
    return tip
  }

  return tip
}

function directionN(point: SVGPoint, node: HTMLElement, target: Element): {top: number; left: number} {
  const bbox = getScreenBBox(target, point)
  return {
    top: bbox.n.y - node.offsetHeight,
    left: bbox.n.x - node.offsetWidth / 2,
  }
}

function initNode(): HTMLElement {
  const node = document.createElement('div')

  node.style.position = 'absolute'
  node.style.top = '0'
  node.style.opacity = '0'
  node.style.pointerEvents = 'none'
  node.style.boxSizing = 'border-box'

  return node
}

// Gets the screen coordinates of a shape.
//
// Given a shape on the screen, will return an SVGPoint for the directions
// n(north), s(south), e(east), w(west), ne(northeast), se(southeast), nw(northwest),
// sw(southwest).
//
//    +-+-+
//    |   |
//    +   +
//    |   |
//    +-+-+
//
// Returns an Object {n, s, e, w, nw, sw, ne, se}
function getScreenBBox(target: Element, point: SVGPoint): BBox {
  let targetel = target
  while ('undefined' === typeof targetel.getScreenCTM && 'undefined' === targetel.parentNode) {
    targetel = targetel.parentNode
  }
  const bbox = {}
  const matrix = targetel.getScreenCTM()
  const tbbox = targetel.getBBox()
  const width = tbbox.width
  const height = tbbox.height
  const x = tbbox.x
  const y = tbbox.y

  point.x = x
  point.y = y
  bbox.nw = point.matrixTransform(matrix)
  point.x += width
  bbox.ne = point.matrixTransform(matrix)
  point.y += height
  bbox.se = point.matrixTransform(matrix)
  point.x -= width
  bbox.sw = point.matrixTransform(matrix)
  point.y -= height / 2
  bbox.w = point.matrixTransform(matrix)
  point.x += width
  bbox.e = point.matrixTransform(matrix)
  point.x -= width / 2
  point.y -= height / 2
  bbox.n = point.matrixTransform(matrix)
  point.y += height
  bbox.s = point.matrixTransform(matrix)

  return bbox
}
